import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Loader2, ShieldCheck, ShieldOff, KeyRound } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import api from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';

const PERMISSIONS = [
  { key: 'create_cari',       label: 'Cari Oluşturma' },
  { key: 'edit_cari',         label: 'Cari Düzenleme' },
  { key: 'delete_cari',       label: 'Cari Silme' },
  { key: 'add_transaction',   label: 'İşlem Ekleme' },
  { key: 'edit_transaction',  label: 'İşlem Düzenleme' },
  { key: 'delete_transaction',label: 'İşlem Silme' },
  { key: 'view_reports',      label: 'Raporları Görme' },
  { key: 'export_excel',      label: 'Excel Dışa Aktarma' },
];

const DEFAULT_PERMS = {
  create_cari: false, edit_cari: false, delete_cari: false,
  add_transaction: true, edit_transaction: false, delete_transaction: false,
  view_reports: false, export_excel: false,
};

const EMPTY_FORM = {
  username: '', full_name: '', password: '', role: 'user',
  permissions: { ...DEFAULT_PERMS },
};

const DATE_FMT = (d) =>
  d ? new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export default function UserManagement() {
  const { toast } = useToast();

  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [toggleTarget, setToggleTarget] = useState(null);
  const [isToggling, setIsToggling] = useState(false);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get('/users');
      setUsers(data.data);
    } catch {
      toast({ title: 'Hata', description: 'Kullanıcılar yüklenemedi.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openAdd = () => {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditTarget(u);
    setForm({
      username: u.username,
      full_name: u.full_name,
      password: '', // boş bırakılırsa değişmez
      role: u.role,
      permissions: { ...DEFAULT_PERMS, ...(u.permissions || {}) },
    });
    setDialogOpen(true);
  };

  const setPermission = (key, checked) => {
    setForm(f => ({ ...f, permissions: { ...f.permissions, [key]: checked } }));
  };

  const handleRoleChange = (role) => {
    if (role === 'admin') {
      // Admin ise tüm izinler otomatik aktif
      setForm(f => ({
        ...f, role,
        permissions: Object.fromEntries(PERMISSIONS.map(p => [p.key, true])),
      }));
    } else {
      setForm(f => ({ ...f, role }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const payload = {
        full_name: form.full_name,
        role: form.role,
        permissions: form.permissions,
      };
      if (form.password) payload.password = form.password;

      if (editTarget) {
        await api.put(`/users/${editTarget.id}`, payload);
        toast({ title: 'Güncellendi', description: `${form.full_name} başarıyla güncellendi.`, variant: 'success' });
      } else {
        await api.post('/users', { ...payload, username: form.username, password: form.password });
        toast({ title: 'Oluşturuldu', description: `${form.full_name} kullanıcısı oluşturuldu.`, variant: 'success' });
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (err) {
      toast({ title: 'Hata', description: err.response?.data?.error || 'İşlem başarısız.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async () => {
    if (!toggleTarget) return;
    setIsToggling(true);
    try {
      if (toggleTarget.is_active) {
        await api.delete(`/users/${toggleTarget.id}`);
        toast({ title: 'Deaktive edildi', description: `${toggleTarget.full_name} pasife alındı.`, variant: 'success' });
      } else {
        await api.put(`/users/${toggleTarget.id}`, { is_active: true, permissions: toggleTarget.permissions });
        toast({ title: 'Aktive edildi', description: `${toggleTarget.full_name} aktive edildi.`, variant: 'success' });
      }
      setToggleTarget(null);
      fetchUsers();
    } catch (err) {
      toast({ title: 'Hata', description: err.response?.data?.error || 'İşlem başarısız.', variant: 'destructive' });
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Başlık */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            Kullanıcı Yönetimi
            <Badge variant="secondary" className="text-xs font-normal">{users.length} kullanıcı</Badge>
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Kullanıcıları ve yetkilerini yönetin</p>
        </div>
        <Button onClick={openAdd} className="gap-2">
          <Plus className="w-4 h-4" /> Yeni Kullanıcı
        </Button>
      </div>

      {/* Tablo */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-slate-500">
              <ShieldCheck className="w-10 h-10 mb-2 opacity-30" />
              <p className="text-sm">Kullanıcı bulunamadı</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ad Soyad</TableHead>
                  <TableHead>Kullanıcı Adı</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Yetkiler</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Kayıt Tarihi</TableHead>
                  <TableHead className="text-right">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} className={!u.is_active ? 'opacity-50' : ''}>
                    <TableCell className="font-medium text-slate-900 dark:text-slate-100">{u.full_name}</TableCell>
                    <TableCell className="text-slate-500 dark:text-slate-400 font-mono text-xs">{u.username}</TableCell>
                    <TableCell>
                      <Badge variant={u.role === 'admin' ? 'default' : 'secondary'}>
                        {u.role === 'admin' ? 'Yönetici' : 'Kullanıcı'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {u.role === 'admin' ? (
                        <span className="text-xs text-slate-500 italic">Tüm yetkiler</span>
                      ) : (
                        <div className="flex flex-wrap gap-1 max-w-56">
                          {PERMISSIONS.filter(p => u.permissions?.[p.key]).map(p => (
                            <span key={p.key} className="text-xs bg-blue-600/10 text-blue-400 border border-blue-600/20 rounded px-1.5 py-0.5">
                              {p.label}
                            </span>
                          ))}
                          {!PERMISSIONS.some(p => u.permissions?.[p.key]) && (
                            <span className="text-xs text-slate-600 italic">Yetki yok</span>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={u.is_active ? 'success' : 'secondary'}>
                        {u.is_active ? 'Aktif' : 'Pasif'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-500 text-xs">{DATE_FMT(u.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-blue-400"
                          onClick={() => openEdit(u)} title="Düzenle">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost" size="icon"
                          className={`h-7 w-7 ${u.is_active ? 'text-slate-400 hover:text-rose-400' : 'text-slate-400 hover:text-emerald-400'}`}
                          onClick={() => setToggleTarget(u)}
                          title={u.is_active ? 'Pasife Al' : 'Aktive Et'}
                        >
                          {u.is_active ? <ShieldOff className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Ekle/Düzenle Modalı */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editTarget ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı Oluştur'}</DialogTitle>
            <DialogDescription>
              {editTarget
                ? 'Kullanıcı bilgilerini ve yetkilerini güncelleyin. Şifre alanını boş bırakırsanız değişmez.'
                : 'Yeni bir kullanıcı oluşturun ve yetkilerini belirleyin.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSave} className="space-y-5 mt-2">
            {/* Temel Bilgiler */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Ad Soyad *</Label>
                <Input placeholder="Ahmet Yılmaz" value={form.full_name}
                  onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} required />
              </div>
              <div className="space-y-1.5">
                <Label>Kullanıcı Adı *</Label>
                <Input placeholder="ahmet" value={form.username}
                  onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                  disabled={!!editTarget} required={!editTarget} />
              </div>
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1">
                  {editTarget && <KeyRound className="w-3 h-3 text-slate-500" />}
                  Şifre {editTarget ? '(değiştirmek için doldurun)' : '*'}
                </Label>
                <Input type="password" placeholder="••••••" value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  required={!editTarget} minLength={editTarget ? undefined : 6} />
              </div>
              <div className="space-y-1.5">
                <Label>Rol *</Label>
                <Select value={form.role} onValueChange={handleRoleChange}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Kullanıcı</SelectItem>
                    <SelectItem value="admin">Yönetici</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Yetkiler (sadece user rolü için) */}
            {form.role !== 'admin' && (
              <div className="space-y-3">
                <div className="border-t border-slate-800 pt-4">
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Kullanıcı Yetkileri</Label>
                  <p className="text-xs text-slate-600 mt-0.5">Kullanıcının erişebileceği işlemleri seçin</p>
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  {PERMISSIONS.map(({ key, label }) => (
                    <label
                      key={key}
                      className="flex items-center gap-2.5 p-2.5 rounded-lg border border-slate-800 hover:bg-slate-800/40 cursor-pointer transition-colors group"
                    >
                      <Checkbox
                        checked={!!form.permissions[key]}
                        onCheckedChange={(checked) => setPermission(key, !!checked)}
                      />
                      <span className="text-sm text-slate-300 group-hover:text-slate-100 transition-colors select-none">
                        {label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {form.role === 'admin' && (
              <div className="rounded-lg bg-blue-950/30 border border-blue-800/30 p-3 text-xs text-blue-400">
                Yönetici rolü tüm yetkilere otomatik olarak sahip olur.
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>İptal</Button>
              <Button type="submit" disabled={isSaving || !form.full_name || (!editTarget && !form.username)}>
                {isSaving
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Kaydediliyor...</>
                  : editTarget ? 'Güncelle' : 'Oluştur'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Aktif/Pasif Onay */}
      <Dialog open={!!toggleTarget} onOpenChange={(o) => !o && setToggleTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className={toggleTarget?.is_active ? 'text-rose-400' : 'text-emerald-400'}>
              {toggleTarget?.is_active ? 'Kullanıcıyı Pasife Al' : 'Kullanıcıyı Aktive Et'}
            </DialogTitle>
            <DialogDescription>
              <strong className="text-slate-200">{toggleTarget?.full_name}</strong>{' '}
              {toggleTarget?.is_active
                ? 'sisteme giriş yapamayacak. Verileri korunacak.'
                : 'yeniden sisteme giriş yapabilecek.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 mt-2">
            <Button variant="outline" onClick={() => setToggleTarget(null)}>İptal</Button>
            <Button
              variant={toggleTarget?.is_active ? 'destructive' : 'success'}
              onClick={handleToggleActive}
              disabled={isToggling}
            >
              {isToggling
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : toggleTarget?.is_active ? 'Pasife Al' : 'Aktive Et'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
